import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import './Onboard.css';

const API_BASE = (process.env.REACT_APP_API_URL || "https://ukd45lve3kvj45tahklem6d67q0fbbxm.lambda-url.us-west-2.on.aws/api/chat").replace('/api/chat', '');

const TOTAL_STEPS = 4;
const MAX_NAME = 50;
const MAX_FREE_TEXT = 1000;

function Progress({ step }) {
    const filled = Math.min(step + 1, TOTAL_STEPS);
    return (
        <div
            className="onboard-progress"
            role="progressbar"
            aria-valuenow={filled}
            aria-valuemin={0}
            aria-valuemax={TOTAL_STEPS}
            aria-label={`Onboarding progress, step ${filled} of ${TOTAL_STEPS}`}
        >
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <span
                    key={i}
                    className={`onboard-progress-segment ${i < filled ? 'filled' : ''}`}
                />
            ))}
        </div>
    );
}

export default function OnboardPage() {
    const auth = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [name, setName] = useState('');
    const [situation, setSituation] = useState('');
    const [frustration, setFrustration] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [opening, setOpening] = useState(null);
    const submittedRef = useRef(false);

    // Skip if already onboarded
    useEffect(() => {
        (async () => {
            try {
                const token = await auth?.getIdToken();
                if (!token) return;
                const res = await fetch(`${API_BASE}/api/profile`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.onboarding?.completed) {
                        navigate('/', { replace: true });
                    }
                }
            } catch (e) {}
        })();
    }, [auth, navigate]);

    const canAdvance = () => {
        if (step === 0) return name.trim().length > 0 && name.trim().length <= MAX_NAME;
        if (step === 1) return situation.trim().length > 0 && situation.trim().length <= MAX_FREE_TEXT;
        if (step === 2) return frustration.trim().length > 0 && frustration.trim().length <= MAX_FREE_TEXT;
        return false;
    };

    const submit = async () => {
        if (submittedRef.current) return;
        submittedRef.current = true;
        setSubmitting(true);
        setError('');
        try {
            const token = await auth.getIdToken();
            const res = await fetch(`${API_BASE}/api/onboard`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name: name.trim(),
                    situation: situation.trim(),
                    frustration: frustration.trim(),
                }),
            });
            if (res.ok) {
                const data = await res.json();
                setOpening(data.opening || null);
            } else if (res.status === 409) {
                setOpening(null);
            } else {
                let msg = 'Something went wrong.';
                try {
                    const j = await res.json();
                    if (j.error) msg = j.error;
                } catch (_) {}
                setError(msg);
                submittedRef.current = false;
            }
        } catch (e) {
            setError('Could not reach server.');
            submittedRef.current = false;
        } finally {
            setSubmitting(false);
        }
    };

    const handleNext = () => {
        setError('');
        if (step === 2) {
            setStep(3);
            submit();
            return;
        }
        if (step < TOTAL_STEPS - 1) {
            setStep(step + 1);
        }
    };

    const handleBack = () => {
        setError('');
        if (step > 0 && step < 3) setStep(step - 1);
    };

    const goToChat = () => navigate('/', { replace: true });
    const skip = () => navigate('/', { replace: true });

    const onTextKeyDown = (e, isMultiline) => {
        if (e.key !== 'Enter') return;
        if (isMultiline && !(e.metaKey || e.ctrlKey)) return;
        e.preventDefault();
        if (canAdvance() && !submitting) handleNext();
    };

    const trimmedName = name.trim();
    const greeting = trimmedName
        ? `Hey ${trimmedName}, nice to meet you`
        : 'Nice to meet you';

    return (
        <div className="onboard">
            <Progress step={step} />

            <div className="onboard-screen">
                {step === 0 && (
                    <div className="onboard-step">
                        <h1 className="onboard-headline">{greeting}</h1>
                        <p className="onboard-sub">What should I call you?</p>
                        <input
                            className="onboard-input"
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value.slice(0, MAX_NAME))}
                            onKeyDown={e => onTextKeyDown(e, false)}
                            placeholder="Your first name"
                            autoFocus
                            maxLength={MAX_NAME}
                            aria-label="First name"
                        />
                    </div>
                )}

                {step === 1 && (
                    <div className="onboard-step">
                        <h1 className="onboard-headline">What does your current dating situation look like?</h1>
                        <p className="onboard-sub">Tell me as much or as little as you want.</p>
                        <textarea
                            className="onboard-textarea"
                            value={situation}
                            onChange={e => setSituation(e.target.value.slice(0, MAX_FREE_TEXT))}
                            onKeyDown={e => onTextKeyDown(e, true)}
                            placeholder="Single, dating around, talking to someone specific..."
                            rows={6}
                            autoFocus
                            maxLength={MAX_FREE_TEXT}
                            aria-label="Your dating situation"
                        />
                        <div className="onboard-counter">{situation.length}/{MAX_FREE_TEXT}</div>
                    </div>
                )}

                {step === 2 && (
                    <div className="onboard-step">
                        <h1 className="onboard-headline">What's frustrating you most about dating and relationships right now?</h1>
                        <p className="onboard-sub">The thing that keeps you up at night, or just bugs you the most.</p>
                        <textarea
                            className="onboard-textarea"
                            value={frustration}
                            onChange={e => setFrustration(e.target.value.slice(0, MAX_FREE_TEXT))}
                            onKeyDown={e => onTextKeyDown(e, true)}
                            placeholder="Something you can't seem to crack..."
                            rows={6}
                            autoFocus
                            maxLength={MAX_FREE_TEXT}
                            aria-label="Your biggest frustration"
                        />
                        <div className="onboard-counter">{frustration.length}/{MAX_FREE_TEXT}</div>
                    </div>
                )}

                {step === 3 && (
                    <div className="onboard-step">
                        <h1 className="onboard-headline">Meet your coach</h1>
                        <p className="onboard-sub">
                            Like a human, the more you talk to him, the more he sees what's right for you.
                        </p>
                        <div className="onboard-coach-block">
                            <h2 className="onboard-coach-title">We told him a little about you — here's what he has to say</h2>
                            {submitting && (
                                <div className="onboard-coach-loading">
                                    <span className="onboard-spinner" aria-hidden="true" />
                                    <span>Preparing your coach…</span>
                                </div>
                            )}
                            {!submitting && opening?.content && (
                                <div className="onboard-coach-message">{opening.content}</div>
                            )}
                            {!submitting && !opening?.content && !error && (
                                <div className="onboard-coach-message subtle">
                                    He's ready when you are. Tap continue to start the conversation.
                                </div>
                            )}
                            {!submitting && error && (
                                <div className="onboard-error">
                                    {error}
                                    <button
                                        className="onboard-link"
                                        onClick={() => { submittedRef.current = false; submit(); }}
                                    >
                                        Try again
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {step !== 3 && error && <div className="onboard-error">{error}</div>}
            </div>

            <div className="onboard-footer">
                <div className="onboard-actions">
                    {step > 0 && step < 3 && (
                        <button className="onboard-btn back" onClick={handleBack}>
                            Back
                        </button>
                    )}
                    {step < 3 && (
                        <button
                            className="onboard-btn next"
                            onClick={handleNext}
                            disabled={!canAdvance() || submitting}
                        >
                            {step === 2 ? 'Meet your coach' : 'Next'}
                        </button>
                    )}
                    {step === 3 && (
                        <button
                            className="onboard-btn next"
                            onClick={goToChat}
                            disabled={submitting}
                        >
                            {opening?.content ? 'Continue to chat' : "Let's get started"}
                        </button>
                    )}
                </div>

                <button className="onboard-skip" onClick={skip}>
                    Skip for now
                </button>
            </div>
        </div>
    );
}
